<template>
  <div
    :id="domId"
    :class="[
      'rounded-lg p-4 mb-3 flex flex-col gap-3',
      theme.cardClass,
    ]"
    role="region"
    :aria-label="theme.label"
  >
    <div class="flex items-start justify-between gap-3 flex-wrap">
      <h4 :class="['font-semibold text-base', theme.headingClass]">
        {{ theme.label }}
      </h4>
      <span
        v-if="ticketDisplay"
        :class="['text-sm font-medium', theme.headingClass]"
      >
        {{ ticketDisplay }}
      </span>
    </div>

    <p :class="['text-sm leading-relaxed', theme.bodyClass]">
      {{ bodyLine }}
    </p>

    <div v-if="hasNotes" class="flex flex-col gap-1">
      <span
        :class="[
          'text-[10px] font-semibold uppercase tracking-wide',
          theme.labelMutedClass,
        ]"
      >
        Customer Notes
      </span>
      <p
        :class="[
          'text-sm italic whitespace-pre-line break-words',
          theme.bodyClass,
        ]"
      >
        &ldquo;{{ complaint.notes }}&rdquo;
      </p>
    </div>

    <div v-if="imageAttachments.length > 0" class="flex flex-col gap-1">
      <span
        :class="[
          'text-[10px] font-semibold uppercase tracking-wide',
          theme.labelMutedClass,
        ]"
      >
        Attachments
      </span>
      <div class="flex flex-wrap gap-1.5">
        <a
          v-for="(att, idx) in imageAttachments"
          :key="att.id ?? `${att.url}-${idx}`"
          :href="resolveUrl(att.url)"
          target="_blank"
          rel="noopener noreferrer"
          :title="att.file_name || ''"
          :aria-label="`Open attachment ${att.file_name || ''}`"
          class="block w-14 h-14 rounded overflow-hidden border border-black/10 bg-white hover:opacity-80 transition-opacity"
        >
          <img
            :src="resolveUrl(att.url)"
            :alt="att.file_name || ''"
            loading="lazy"
            class="w-full h-full object-cover"
            @error="onImageError"
          />
        </a>
      </div>
    </div>

    <p :class="['text-xs', theme.subtextClass]">
      {{ theme.subtext }}
    </p>
  </div>
</template>

<script setup lang="ts">
import {
  buildComplaintBodyLine,
  formatComplaintTicketNo,
  getComplaintCardDomId,
  getComplaintStatusTheme,
  getImageAttachments,
} from '@/composables';
import { Complaint } from '@/models';
import { computed } from 'vue';

interface Props {
  complaint: Complaint;
}

const props = defineProps<Props>();

const storageUrl = (import.meta.env.VITE_APP_FILE_URL ?? '').replace(/\/+$/, '');

const theme = computed(() => getComplaintStatusTheme(props.complaint?.status));

const ticketDisplay = computed(() =>
  formatComplaintTicketNo(props.complaint?.ticket_no)
);

const bodyLine = computed(() => buildComplaintBodyLine(props.complaint));

const hasNotes = computed(() => {
  const n = props.complaint?.notes;
  return typeof n === 'string' && n.trim().length > 0;
});

const imageAttachments = computed(() => getImageAttachments(props.complaint));

const domId = computed(() => getComplaintCardDomId(props.complaint));

const resolveUrl = (url: string): string => {
  if (!url) return '';
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  const cleaned = url.replace(/^\/+/, '');
  return storageUrl ? `${storageUrl}/${cleaned}` : `/${cleaned}`;
};

const onImageError = (e: Event) => {
  const target = e.target as HTMLImageElement | null;
  if (target) {
    target.style.display = 'none';
  }
};
</script>
